import type React from 'react'
import type { RefObject } from 'react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import '@glideapps/glide-data-grid/dist/index.css'
import {
  DataEditor,
  type EditableGridCell,
  type GridCell,
  GridCellKind,
  type GridColumn,
  type Item,
  type Rectangle
} from '@glideapps/glide-data-grid'
import { dataColumnOfOverlay, enumOptionsFor, isKnownEnumValue } from './MetadataHandling/EnumColumns.js'
import { isClearToken } from './MetadataHandling/CSVClearSentinel.js'
import type { ColumnFilters, ColumnSort } from './PoolCsvGridView.ts'
import { activeFilters, nextSort, validateNewColumn, visibleRowIndices } from './PoolCsvGridView.ts'
import { EnumCellEditor } from './EnumCellEditor.js'
import type { CSVMode } from '../../HomePage/Modals/CSVModalTypes.js'
import type { BaseModalProps } from '../../../Modal/BaseModal.js'
import { BaseModal } from '../../../Modal/BaseModal.js'

export interface EditPoolCsvModalProps {
  /** What the rows belong to, e.g. `Order <id>` or `Project <id>`. Display only. */
  contextLabel: string
  csvMode: CSVMode
  initialColumns: string[]
  initialRows: Record<string, string>[]
  /** Extra line under the header, for a caller whose rows are a subset of what the page shows. */
  notice?: string
}

export interface EditPoolCsvModalResult {
  rows: Record<string, string>[]
}

/** `rowMarkers="number"` on the DataEditor below prepends one column to glide's own coordinates. */
const ROW_MARKER_COLUMNS = 1

const READ_ONLY_COLUMNS = new Set(['sampleId', 'sequenceId', 'forwardFile', 'reverseFile'])

/* the import applies types and pools only while the lab can still change the order's items */
const STRUCTURE_COLUMNS = ['type', 'pool']

const readOnlyColumnsFor = (csvMode: CSVMode) =>
  csvMode === 'CREATE_SAMPLES' ? READ_ONLY_COLUMNS : new Set([...READ_ONLY_COLUMNS, ...STRUCTURE_COLUMNS])

/** Label only — the handler accepts either modifier. */
const searchShortcutLabel = () =>
  typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.userAgent) ? '\u2318F' : 'Ctrl+F'

const WIDE_COLUMNS = new Set([
  'customerSampleName',
  'forwardFile',
  'reverseFile',
  'forwardPrimer',
  'reversePrimer',
  'forwardPrimerRequested',
  'reversePrimerRequested'
])

const NARROW_COLUMNS = new Set(['lat', 'lon', 'type'])

const getColumnWidth = (column: string) => {
  if (WIDE_COLUMNS.has(column)) return 220
  if (NARROW_COLUMNS.has(column)) return 120
  if (column.toLowerCase().includes('date')) return 140
  if (column.toLowerCase().includes('time')) return 140
  return 160
}

const getModeLabel = (csvMode: CSVMode) => {
  switch (csvMode) {
    case 'UPDATE_METADATA':
      return 'Update Sample Metadata'
    case 'PREPARE_METADATA':
      return 'Prepare Sample Metadata'
    case 'CREATE_SAMPLES':
      return 'Create Samples'
    default:
      return csvMode
  }
}

const normalizeRows = (columns: string[], rows: Record<string, string>[]) =>
  rows.map((row) => {
    const normalized: Record<string, string> = {}
    columns.forEach((column) => {
      normalized[column] = row[column] ?? ''
    })
    return normalized
  })

export const EditPoolCsvModal: React.FC<EditPoolCsvModalProps & BaseModalProps<EditPoolCsvModalResult>> = ({
  contextLabel,
  csvMode,
  initialColumns,
  initialRows,
  notice,
  isOpen,
  onClose
}) => {
  const [columnsOrder, setColumnsOrder] = useState<string[]>(initialColumns)
  const readOnlyColumns = useMemo(() => readOnlyColumnsFor(csvMode), [csvMode])
  const [rows, setRows] = useState<Record<string, string>[]>(() => normalizeRows(initialColumns, initialRows))
  const [searchValue, setSearchValue] = useState('')
  const [showSearch, setShowSearch] = useState(false)
  const [columnWidths, setColumnWidths] = useState<Record<string, number>>({})
  const [sort, setSort] = useState<ColumnSort | null>(null)
  const [filters, setFilters] = useState<ColumnFilters>({})
  const [filterColumn, setFilterColumn] = useState('')
  const [newColumnName, setNewColumnName] = useState<string | null>(null)
  const [newColumnError, setNewColumnError] = useState<string | null>(null)
  const editorPortalRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!isOpen) return
    setColumnsOrder(initialColumns)
    setRows(normalizeRows(initialColumns, initialRows))
    setSearchValue('')
    setShowSearch(false)
    setColumnWidths({})
    setSort(null)
    setFilters({})
    setFilterColumn('')
    setNewColumnName(null)
    setNewColumnError(null)
  }, [isOpen, initialColumns, initialRows])

  /*
   * The grid renders a view, never the rows themselves: this is the source index behind each grid
   * row, and every coordinate below is mapped through it before a cell is read or written. Editing
   * is live against `rows`, so a row that stops matching its filter does leave the view.
   */
  const visibleRows = useMemo(() => visibleRowIndices(rows, filters, sort), [rows, filters, sort])

  const gridColumns = useMemo<GridColumn[]>(
    () =>
      columnsOrder.map((column) => ({
        id: column,
        title: `${column}${sort?.column === column ? (sort.direction === 'asc' ? ' \u25b2' : ' \u25bc') : ''}`,
        width: columnWidths[column] ?? getColumnWidth(column)
      })),
    [columnsOrder, columnWidths, sort]
  )

  /* Unmangled already — glide subtracts the row-marker offset before calling this. */
  const onHeaderClicked = useCallback(
    (column: number) => {
      const key = columnsOrder[column]
      if (key !== undefined) setSort((prev) => nextSort(prev, key))
    },
    [columnsOrder]
  )

  const getCellContent = useCallback(
    ([col, row]: Item): GridCell => {
      const columnKey = columnsOrder[col]
      const value = rows[visibleRows[row]]?.[columnKey] ?? ''

      /*
       * An enum column shows what the import will do with the cell: a value that is neither a known
       * key, nor empty, nor the clear token is rejected at import time, and finding that out here is
       * the difference between fixing one cell and re-running the whole file.
       */
      const rejected = !isClearToken(value) && value !== '' && !isKnownEnumValue(columnKey, value)

      return {
        kind: GridCellKind.Text,
        data: value,
        displayData: value,
        allowOverlay: !readOnlyColumns.has(columnKey),
        readonly: readOnlyColumns.has(columnKey),
        themeOverride: rejected ? { bgCell: '#fee2e2', textDark: '#991b1b' } : undefined
      }
    },
    [columnsOrder, rows, visibleRows, readOnlyColumns]
  )

  /** A closed-set column edits through a dropdown; everything else keeps the default text editor. */
  const provideEditor = useCallback(
    (cell: GridCell & { location?: Item }) => {
      const [overlayColumn, row] = cell.location ?? []
      if (overlayColumn === undefined || row === undefined || cell.kind !== GridCellKind.Text) return undefined

      const column = dataColumnOfOverlay({
        overlayColumn,
        markerColumns: ROW_MARKER_COLUMNS,
        columnsOrder,
        row: rows[visibleRows[row]],
        cellText: cell.data
      })
      const options = column === undefined ? undefined : enumOptionsFor(column)
      if (!options) return undefined

      const Editor = (p: {
        value: GridCell
        onChange: (newValue: EditableGridCell) => void
        onFinishedEditing: (newValue?: EditableGridCell) => void
      }) => (
        <EnumCellEditor
          value={p.value.kind === GridCellKind.Text ? p.value.data : ''}
          options={options}
          onChange={p.onChange}
          onFinishedEditing={p.onFinishedEditing}
        />
      )
      Editor.displayName = 'EnumCellEditorOverlay'
      return Editor
    },
    [columnsOrder, rows, visibleRows]
  )

  const onCellEdited = useCallback(
    ([col, row]: Item, newValue: EditableGridCell) => {
      const columnKey = columnsOrder[col]
      if (readOnlyColumns.has(columnKey)) return
      if (newValue.kind !== GridCellKind.Text) return

      const nextValue = newValue.data ?? ''
      const target = visibleRows[row]
      if (target === undefined) return

      setRows((prev) => {
        if (prev[target]?.[columnKey] === nextValue) return prev
        const next = [...prev]
        next[target] = {
          ...next[target],
          [columnKey]: nextValue
        }
        return next
      })
    },
    [columnsOrder, visibleRows, readOnlyColumns]
  )

  const getCellsForSelection = useCallback(
    (selection: Rectangle): GridCell[][] => {
      const result: GridCell[][] = []

      for (let y = selection.y; y < selection.y + selection.height; y++) {
        const row: GridCell[] = []
        for (let x = selection.x; x < selection.x + selection.width; x++) {
          row.push(getCellContent([x, y]))
        }
        result.push(row)
      }

      return result
    },
    [getCellContent]
  )

  const onPaste = useCallback(
    (target: Item, values: readonly (readonly string[])[]) => {
      const [targetCol, targetRow] = target

      setRows((prev) => {
        const next = [...prev]

        values.forEach((pasteRow, rowOffset) => {
          /* Paste walks DOWN THE VIEW, so each offset resolves to whichever source row is shown there. */
          const actualRow = visibleRows[targetRow + rowOffset]
          if (actualRow === undefined) return

          let nextRow = next[actualRow]

          pasteRow.forEach((rawValue, colOffset) => {
            const actualCol = targetCol + colOffset
            if (actualCol >= columnsOrder.length) return

            const columnKey = columnsOrder[actualCol]
            if (readOnlyColumns.has(columnKey)) return

            const value = rawValue ?? ''
            if (nextRow[columnKey] !== value) {
              if (nextRow === next[actualRow]) {
                nextRow = { ...nextRow }
                next[actualRow] = nextRow
              }
              nextRow[columnKey] = value
            }
          })
        })

        return next
      })

      return true
    },
    [columnsOrder, visibleRows, readOnlyColumns]
  )

  const onColumnResize = useCallback((column: GridColumn, newSize: number) => {
    const key = String(column.id)
    setColumnWidths((prev) => ({
      ...prev,
      [key]: newSize
    }))
  }, [])

  const onColumnMoved = useCallback((startIndex: number, endIndex: number) => {
    setColumnsOrder((prev) => {
      const next = [...prev]
      const [moved] = next.splice(startIndex, 1)
      next.splice(endIndex, 0, moved)
      return next
    })
  }, [])

  /*
   * A new column is a new CSV header, which the import step then offers under "Custom Metadata" —
   * nothing here has to know about custom attributes, and nothing is written until that step maps it.
   */
  const addColumn = () => {
    const result = validateNewColumn(newColumnName ?? '', columnsOrder)
    if (!result.ok) {
      setNewColumnError(result.error)
      return
    }
    setColumnsOrder((prev) => [...prev, result.name])
    setRows((prev) => prev.map((row) => ({ ...row, [result.name]: '' })))
    setNewColumnName(null)
    setNewColumnError(null)
  }

  const submit = () => {
    const normalized = rows.map((row) => {
      const nextRow: Record<string, string> = {}
      columnsOrder.forEach((column) => {
        nextRow[column] = row[column] ?? ''
      })
      return nextRow
    })

    onClose({
      rows: normalized
    } as EditPoolCsvModalResult)
  }

  function ensurePortal(ref: RefObject<HTMLDivElement | null>): ref is RefObject<HTMLDivElement> {
    return ref.current != null
  }

  return (
    <BaseModal isOpen={isOpen} onClose={() => onClose()} closeOnEscape={false}>
      <div className="modal-box w-[98vw] max-w-[98vw] h-[92vh] p-0 flex flex-col overflow-hidden">
        <button onClick={() => onClose()} className="btn btn-sm btn-circle btn-ghost absolute right-4 top-4 z-10" aria-label="Close Modal">
          ✕
        </button>

        <div className="border-b border-base-300 px-5 py-4 pr-16">
          <h3 className="text-lg font-bold">Edit Data</h3>
          <div className="mt-1 text-sm opacity-80">
            <span className="font-mono">{contextLabel}</span> · {getModeLabel(csvMode)}
          </div>
          <div className="mt-2 text-sm opacity-70">
            Edit the generated pool CSV directly in the grid. Read-only columns: {[...readOnlyColumns].join(', ')}.
          </div>
          {notice && <div className="mt-2 text-sm text-warning">{notice}</div>}
        </div>

        <div className="border-b border-base-300 px-5 py-3 flex flex-col gap-3">
          <div className="flex flex-wrap items-center gap-2">
            <button data-testid="csv-grid-search" className="btn btn-sm btn-outline gap-2" onClick={() => setShowSearch(true)}>
              Search
              <kbd className="kbd kbd-xs">{searchShortcutLabel()}</kbd>
            </button>

            <select
              aria-label="Filter column"
              data-testid="csv-grid-filter-column"
              className="select select-bordered select-sm"
              value={filterColumn}
              onChange={(e) => setFilterColumn(e.target.value)}
            >
              <option value="">Filter a column…</option>
              {columnsOrder.map((column) => (
                <option key={column} value={column}>
                  {column}
                </option>
              ))}
            </select>
            <input
              type="text"
              aria-label="Filter value"
              data-testid="csv-grid-filter-value"
              className="input input-bordered input-sm w-full sm:w-52"
              placeholder={filterColumn === '' ? 'Pick a column first' : `Contains…`}
              disabled={filterColumn === ''}
              value={filters[filterColumn] ?? ''}
              onChange={(e) => setFilters((prev) => ({ ...prev, [filterColumn]: e.target.value }))}
            />

            {newColumnName === null ? (
              <button data-testid="csv-grid-add-column" className="btn btn-sm btn-outline" onClick={() => setNewColumnName('')}>
                Add column
              </button>
            ) : (
              <>
                <input
                  type="text"
                  autoFocus
                  aria-label="New column name"
                  data-testid="csv-grid-new-column-name"
                  className="input input-bordered input-sm w-full sm:w-52"
                  placeholder="New column name"
                  value={newColumnName}
                  onChange={(e) => {
                    setNewColumnName(e.target.value)
                    setNewColumnError(null)
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') addColumn()
                    if (e.key === 'Escape') {
                      setNewColumnName(null)
                      setNewColumnError(null)
                    }
                  }}
                />
                <button data-testid="csv-grid-new-column-confirm" className="btn btn-sm btn-primary" onClick={addColumn}>
                  Add
                </button>
                <button
                  className="btn btn-sm btn-ghost"
                  onClick={() => {
                    setNewColumnName(null)
                    setNewColumnError(null)
                  }}
                >
                  Cancel
                </button>
              </>
            )}

            <div data-testid="csv-grid-counts" className="ml-auto text-sm opacity-70 whitespace-nowrap">
              {visibleRows.length === rows.length ? `${rows.length} rows` : `${visibleRows.length} of ${rows.length} rows`} ·{' '}
              {columnsOrder.length} columns
            </div>
          </div>

          {newColumnError && (
            <div data-testid="csv-grid-column-error" className="text-sm text-error">
              {newColumnError}
            </div>
          )}

          {activeFilters(filters).length > 0 && (
            <div className="flex flex-wrap items-center gap-2">
              {activeFilters(filters).map(([column, needle]) => (
                <button
                  key={column}
                  className="badge badge-neutral gap-1"
                  data-testid="csv-grid-filter-chip"
                  onClick={() => setFilters((prev) => ({ ...prev, [column]: '' }))}
                  aria-label={`Clear the ${column} filter`}
                >
                  {column}: {needle} ✕
                </button>
              ))}
              <button data-testid="csv-grid-clear-filters" className="btn btn-ghost btn-xs" onClick={() => setFilters({})}>
                Clear all
              </button>
            </div>
          )}
        </div>

        <div className="flex-1 min-h-0 p-4 flex">
          <div
            className="min-h-0 flex-1 rounded-box border border-base-300 overflow-hidden bg-base-100"
            onKeyDown={(e) => {
              if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'f') {
                e.preventDefault()
                setShowSearch(true)
              }
            }}
          >
            {ensurePortal(editorPortalRef) && (
              <DataEditor
                columns={gridColumns}
                rows={visibleRows.length}
                getCellContent={getCellContent}
                onCellEdited={onCellEdited}
                provideEditor={provideEditor}
                getCellsForSelection={getCellsForSelection}
                onPaste={onPaste}
                onColumnResize={onColumnResize}
                onColumnMoved={onColumnMoved}
                onHeaderClicked={onHeaderClicked}
                smoothScrollX={true}
                smoothScrollY={true}
                rowMarkers="number"
                rowMarkerStartIndex={1}
                rowSelect="multi"
                rangeSelect="rect"
                columnSelect="single"
                freezeColumns={2}
                overscrollX={300}
                overscrollY={120}
                showSearch={showSearch}
                onSearchClose={() => setShowSearch(false)}
                searchValue={searchValue}
                onSearchValueChange={setSearchValue}
                maxColumnWidth={420}
                minColumnWidth={90}
                width="100%"
                height="100%"
                portalElementRef={editorPortalRef}
                keybindings={{
                  search: false,
                  copy: true,
                  paste: true,
                  downFill: false
                }}
              />
            )}
          </div>
        </div>

        <div className="border-t border-base-300 px-5 py-4 flex flex-col-reverse gap-2 sm:flex-row sm:justify-between sm:items-center">
          <div className="text-xs opacity-70">
            Tip: paste from spreadsheets directly into the grid. Reorder or resize columns as needed.
          </div>

          <div className="flex justify-end gap-2">
            <button className="btn btn-ghost" onClick={() => onClose()}>
              Cancel
            </button>
            <button data-testid="csv-grid-submit" className="btn btn-primary" onClick={submit}>
              Use Edited Data
            </button>
          </div>
        </div>
      </div>
      <div
        ref={editorPortalRef}
        className="pool-csv-editor-portal"
        style={{
          position: 'fixed',
          inset: 0,
          zIndex: 10_000,
          pointerEvents: 'none'
        }}
      />
    </BaseModal>
  )
}
